import { useEffect, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Radio,
  Select,
  Stack,
  Text,
} from '@mantine/core';

import { CountryPill } from '@/components/CountryPill';
import { QuotedMessage } from '@/components/RegistryOrders/RegistryOrdersCard/QuotedMessage';
import { StatusBadge } from '@/components/StatusBadge';
import type { RegistryInboundAttachTarget } from '@/hooks/useRegistryInboundAttachDialog';
import { useRegistryOrderSearch } from '@/hooks/useRegistryOrderSearch';
import { RegistryOrderError } from '@/services/registry-order-error';
import {
  describeSuggestionWhy,
  REGISTRY_ORDER_STATUS_META,
  registryOrderCountry,
  type RegistryInboundRow,
  type RegistryInboundSuggestion,
  type RegistryOrderQueueRow,
} from '@/types/registry-documents';

interface Failure {
  message: string;
  /** Nothing a second attempt would change: the email is no longer open. */
  final: boolean;
}

/** What a refused attach means to the person, by its kind. */
export function describeAttachFailure(err: unknown): Failure {
  if (err instanceof RegistryOrderError) {
    if (err.kind === 'already_resolved') {
      return { message: 'Someone already dealt with this email.', final: true };
    }
    if (err.kind === 'not_prepared') {
      return {
        message: 'That order has not been sent yet - pick one that was placed.',
        final: false,
      };
    }
    if (err.kind === 'not_found') {
      return {
        message: 'The email or the order could not be found - it may have gone meanwhile.',
        final: false,
      };
    }
  }
  return { message: err instanceof Error ? err.message : 'The request failed', final: false };
}

/** The label a searched order has in the picker. */
function optionLabel(order: RegistryOrderQueueRow): string {
  const parts = [order.reference, order.companyName, order.reportLabel].filter(Boolean);
  return parts.join(' · ');
}

interface RegistryInboundAttachModalProps {
  opened: boolean;
  onClose: () => void;
  /** The email being attached. Null before the first one is chosen; kept
   * by the caller while the dialog closes, so its preview does not blank. */
  row: RegistryInboundRow | null;
  /** Throws on failure; the modal stays open and shows the message. */
  onConfirm: (target: RegistryInboundAttachTarget) => Promise<unknown>;
}

/**
 * Put an email that reached no order onto its order. The orders the API
 * suggests (the same company name, the same email thread) are one click
 * away; any other order is found by searching the queue. A suggestion is
 * never applied on its own: the choice is the person's, and it is
 * recorded so the suggestions can be measured.
 *
 * Attaching moves the email and its files onto the order and nothing
 * else: the order's status is not touched, and is reviewed on the report.
 */
export function RegistryInboundAttachModal({
  opened,
  onClose,
  row,
  onConfirm,
}: Readonly<RegistryInboundAttachModalProps>) {
  const [suggestionId, setSuggestionId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<RegistryOrderQueueRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  // A picked order ends the search: its label is in the box, and is not a
  // search for anything.
  const found = useRegistryOrderSearch(picked ? '' : search, opened);

  // Cleared on close, so reopening for another email never inherits a choice.
  useEffect(() => {
    if (!opened) {
      setSuggestionId(null);
      setSearch('');
      setPicked(null);
      setFailure(null);
    }
  }, [opened]);

  const suggestions = row?.suggestions ?? [];
  const suggested = suggestions.find((entry) => entry.orderId === suggestionId) ?? null;
  const target = targetOf(suggested, picked);
  const canSubmit = target !== null && !submitting && failure?.final !== true;

  const chooseSuggestion = (orderId: string) => {
    setSuggestionId(orderId);
    setPicked(null);
    setSearch('');
  };

  const choosePicked = (orderId: string | null) => {
    const order = orderId === null ? null : (found.rows.find((r) => r.id === orderId) ?? null);
    setPicked(order);
    if (order) {
      setSuggestionId(null);
    }
  };

  const submit = async () => {
    if (!target) {
      return;
    }
    setSubmitting(true);
    setFailure(null);
    try {
      await onConfirm(target);
    } catch (err) {
      setFailure(describeAttachFailure(err));
    } finally {
      setSubmitting(false);
    }
  };

  // An attach in flight is not abandoned by dismissing the dialog: its
  // outcome, a refusal above all, must still have somewhere to be shown.
  const dismiss = () => {
    if (!submitting) {
      onClose();
    }
  };

  // The option list keeps the picked order while a new search replaces
  // the rows, so the box never shows a value it cannot name.
  const options = [...(picked ? [picked] : []), ...found.rows.filter((r) => r.id !== picked?.id)];

  return (
    <Modal opened={opened} onClose={dismiss} title="Attach email to an order" size="lg">
      <Stack>
        {row && <EmailPreview row={row} />}
        {suggestions.length > 0 && (
          <Radio.Group
            label="Suggested orders"
            value={suggestionId ?? ''}
            onChange={chooseSuggestion}
          >
            <Stack gap="xs" mt="xs">
              {suggestions.map((suggestion) => (
                <Radio
                  key={suggestion.orderId}
                  value={suggestion.orderId}
                  label={<SuggestionLabel suggestion={suggestion} />}
                  disabled={submitting}
                  data-testid={`registry-inbound-attach-suggestion-${suggestion.orderId}`}
                />
              ))}
            </Stack>
          </Radio.Group>
        )}
        <Select
          label={suggestions.length > 0 ? 'Or search another order' : 'Search for the order'}
          placeholder="Company or GTI reference…"
          searchable
          clearable
          data={options.map((order) => ({ value: order.id, label: optionLabel(order) }))}
          // The server searched already; nothing is filtered away here.
          filter={({ options: all }) => all}
          value={picked?.id ?? null}
          onChange={choosePicked}
          searchValue={search}
          onSearchChange={(value) => {
            setSearch(value);
            if (picked && value !== optionLabel(picked)) {
              setPicked(null);
            }
          }}
          nothingFoundMessage={nothingFound(search, found.isLoading, found.error)}
          rightSection={found.isLoading ? <Loader size="xs" /> : undefined}
          disabled={submitting}
          comboboxProps={{ withinPortal: false }}
          data-testid="registry-inbound-attach-search"
        />
        <Text size="xs" c="dimmed">
          The email and any attached files move onto the order. The order&apos;s status is not
          changed - review it on the report.
        </Text>
        {failure && (
          <Alert
            color={failure.final ? 'orange' : 'red'}
            title="The email was not attached"
            data-testid="registry-inbound-attach-failure"
          >
            {failure.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={dismiss} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={!canSubmit}
            loading={submitting}
            data-testid="registry-inbound-attach-confirm"
          >
            {target ? `Attach to ${target.reference}` : 'Attach to order'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** The one order chosen, from a suggestion or from the search. */
function targetOf(
  suggested: RegistryInboundSuggestion | null,
  picked: RegistryOrderQueueRow | null
): RegistryInboundAttachTarget | null {
  if (suggested) {
    return {
      orderId: suggested.orderId,
      reference: suggested.reference,
      reportLabel: suggested.reportLabel,
    };
  }
  if (picked) {
    return { orderId: picked.id, reference: picked.reference, reportLabel: picked.reportLabel };
  }
  return null;
}

/** What the empty dropdown says: a prompt before anything is typed, the
 * search still running, its failure, or no match. */
function nothingFound(search: string, loading: boolean, error: Error | null): string {
  if (search.trim() === '') {
    return 'Type a company name or GTI reference';
  }
  if (loading) {
    return 'Searching…';
  }
  if (error) {
    return `Search failed: ${error.message}`;
  }
  return 'No order matches';
}

/** The email being attached: its subject, then who wrote, when, and the
 * start of what they wrote. */
function EmailPreview({ row }: Readonly<{ row: RegistryInboundRow }>) {
  return (
    <Stack gap={4} data-testid="registry-inbound-attach-preview">
      {row.subject === null ? (
        <Text size="sm" c="dimmed">
          Details not available yet
        </Text>
      ) : (
        <Text size="sm" fw={600}>
          {row.subject}
        </Text>
      )}
      <QuotedMessage
        message={{ sender: row.sender, receivedAt: row.receivedAt, text: row.excerpt ?? '' }}
        fallbackFacts="Sender and time not recorded"
      />
    </Stack>
  );
}

function SuggestionLabel({ suggestion }: Readonly<{ suggestion: RegistryInboundSuggestion }>) {
  const meta = REGISTRY_ORDER_STATUS_META[suggestion.status] ?? {
    label: suggestion.status,
    color: 'gray',
  };
  return (
    <Group gap={6} wrap="wrap">
      <Text size="sm" ff="monospace">
        {suggestion.reference}
      </Text>
      <Text size="sm">{suggestion.companyName}</Text>
      <CountryPill country={registryOrderCountry(suggestion.countryCode)} />
      <StatusBadge label={meta.label} color={meta.color} size="xs" />
      {suggestion.reportLabel && (
        <Text size="xs" c="dimmed">
          {suggestion.reportLabel}
        </Text>
      )}
      <Badge size="xs" variant="light" color="blue">
        {describeSuggestionWhy(suggestion.why)}
      </Badge>
    </Group>
  );
}
